import React from "react";
import {Badge, Button, Card, CardBody, CardHeader, Col, Container, FormGroup, Input, Label, ListGroup, ListGroupItem, Row} from "reactstrap";
import {inject, observer} from "mobx-react";
import EmployeAPI from "../../../util/employe";

// Sections du tableau de bord (colonnes de la table modules) : ce que l'employé voit dans le menu
const SECTIONS = {
    document: "Documents : tickets, devis, factures, SMS",
    stock: "Stock, commandes fournisseurs, casiers",
    boutique: "Commandes de la boutique en ligne",
    board: "Tableau des prises en charge",
    print: "Impression",
    web_client: "Site web : pages, carrousel, fournisseurs",
    blog: "Blog et bric à brac",
    espace_client: "Espace client",
};

const VIDE = {id: null, nom: "", prenom: "", email: "", password: "", ban: false, modules: {document: true}, droits: []};

/**
 * Comptes du personnel : un employé voit les sections cochées, et n'a que les droits cochés (les chiffres de
 * l'entreprise, par exemple). Réservé aux administrateurs ; un compte ne se supprime pas, il se désactive.
 */
const Employes = (inject("rootStore")(observer(class Employes extends React.Component {

    api = new EmployeAPI();

    get injected() {
        return this.props;
    }

    constructor(props, context) {
        super(props, context);
        this.injected.rootStore.savedDatasStore.setHeader("Employés", "Comptes du personnel et ce qu'ils peuvent faire", "default.jpg");
        this.state = {employes: [], droits: {}, fiche: {...VIDE}, enregistrement: false};
    }

    componentDidMount() {
        this.charger();
    }

    async charger() {
        try {
            const reponse = await this.api.getAll();
            this.setState({employes: reponse.datas, droits: reponse.droits});
        } catch (e) {
            this.erreur(e);
        }
    }

    erreur(e) {
        this.injected.rootStore.notificationStore.setNotification(e?.response?.data?.message ?? "Erreur", "error");
    }

    choisir(employe) {
        this.setState({fiche: {...employe, password: ""}});
    }

    changer(champ, valeur) {
        this.setState({fiche: {...this.state.fiche, [champ]: valeur}});
    }

    cocherSection(section, coche) {
        this.changer("modules", {...this.state.fiche.modules, [section]: coche});
    }

    cocherDroit(droit, coche) {
        const droits = this.state.fiche.droits.filter(d => d !== droit);
        this.changer("droits", coche ? [...droits, droit] : droits);
    }

    async enregistrer() {
        const fiche = this.state.fiche;
        const modules = {};
        Object.keys(SECTIONS).forEach(section => modules[section] = !!fiche.modules[section]);
        const donnees = {id: fiche.id, nom: fiche.nom, prenom: fiche.prenom, email: fiche.email, ban: fiche.ban, modules, droits: fiche.droits};
        if (fiche.password) {
            donnees.password = fiche.password;
        }

        this.setState({enregistrement: true});
        try {
            const reponse = await this.api.save(donnees);
            this.injected.rootStore.notificationStore.setNotification("Employé enregistré", "success");
            this.setState({fiche: {...reponse.datas, password: ""}});
            await this.charger();
        } catch (e) {
            this.erreur(e);
        } finally {
            this.setState({enregistrement: false});
        }
    }

    render() {
        const {employes, droits, fiche} = this.state;
        const nouveau = !fiche.id;

        return (
            <Container className="mt-2" fluid>
                <Row>
                    <Col md="4">
                        <Card className="shadow mb-3">
                            <CardHeader style={{display: "flex", justifyContent: "space-between"}}>
                                <h4 className="mb-0">Employés</h4>
                                <Button size="sm" color="primary" onClick={() => this.setState({fiche: {...VIDE}})}>Nouveau</Button>
                            </CardHeader>
                            <ListGroup flush>
                                {employes.map(e => (
                                    <ListGroupItem key={e.id} action active={e.id === fiche.id} tag="button" onClick={() => this.choisir(e)}>
                                        {e.prenom} {e.nom}
                                        {e.ban && <Badge color="secondary" className="ml-2">désactivé</Badge>}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                            {employes.length === 0 && <CardBody className="text-muted">Aucun employé pour l'instant.</CardBody>}
                        </Card>
                    </Col>

                    <Col md="8">
                        <Card className="shadow mb-3">
                            <CardHeader><h4 className="mb-0">{nouveau ? "Nouvel employé" : fiche.prenom + " " + fiche.nom}</h4></CardHeader>
                            <CardBody>
                                <Row>
                                    <Col md="6"><FormGroup>
                                        <Label>Prénom</Label>
                                        <Input value={fiche.prenom} onChange={e => this.changer("prenom", e.target.value)}/>
                                    </FormGroup></Col>
                                    <Col md="6"><FormGroup>
                                        <Label>Nom</Label>
                                        <Input value={fiche.nom} onChange={e => this.changer("nom", e.target.value)}/>
                                    </FormGroup></Col>
                                    <Col md="6"><FormGroup>
                                        <Label>E-mail (identifiant de connexion)</Label>
                                        <Input type="email" value={fiche.email} onChange={e => this.changer("email", e.target.value)}/>
                                    </FormGroup></Col>
                                    <Col md="6"><FormGroup>
                                        <Label>{nouveau ? "Mot de passe (12 caractères au moins)" : "Nouveau mot de passe (laisser vide pour garder l'actuel)"}</Label>
                                        <Input type="password" autoComplete="new-password" value={fiche.password}
                                               onChange={e => this.changer("password", e.target.value)}/>
                                    </FormGroup></Col>
                                </Row>

                                <h5 className="mt-2">Sections visibles</h5>
                                <Row>
                                    {Object.keys(SECTIONS).map(section => (
                                        <Col md="6" key={section}>
                                            <FormGroup check>
                                                <Label check>
                                                    <Input type="checkbox" checked={!!fiche.modules[section]}
                                                           onChange={e => this.cocherSection(section, e.target.checked)}/>
                                                    {SECTIONS[section]}
                                                </Label>
                                            </FormGroup>
                                        </Col>
                                    ))}
                                </Row>

                                <h5 className="mt-3">Droits</h5>
                                <p className="text-muted mb-1"><small>Sans droit coché, l'employé ne voit pas ces informations.</small></p>
                                {Object.keys(droits).map(droit => (
                                    <FormGroup check key={droit}>
                                        <Label check>
                                            <Input type="checkbox" checked={fiche.droits.includes(droit)}
                                                   onChange={e => this.cocherDroit(droit, e.target.checked)}/>
                                            {droits[droit]}
                                        </Label>
                                    </FormGroup>
                                ))}

                                {!nouveau && <FormGroup check className="mt-3">
                                    <Label check>
                                        <Input type="checkbox" checked={!!fiche.ban} onChange={e => this.changer("ban", e.target.checked)}/>
                                        Compte désactivé (l'employé ne peut plus se connecter)
                                    </Label>
                                </FormGroup>}

                                <Button color="success" className="mt-3" onClick={() => this.enregistrer()}
                                        disabled={this.state.enregistrement || !fiche.nom || !fiche.prenom || !fiche.email || (nouveau && fiche.password.length < 12)}>
                                    {nouveau ? "Créer le compte" : "Enregistrer"}
                                </Button>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        );
    }
})));

export default Employes;
